import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PlatformLocation } from '@angular/common';
import { Observable, catchError, of, tap, switchMap } from 'rxjs';
import { ClientDetails, ClientDetailsResponse } from '../interfaces/client-details.interface';
import { environment } from '../../../environments/environment';
import { LogoUtilityService, LogoInfo } from './logo-utility.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class ClientDetailsService {
  private readonly http = inject(HttpClient);
  private readonly logoUtility = inject(LogoUtilityService);
  private readonly configService = inject(ConfigService);
  private readonly platformLocation = inject(PlatformLocation);
  
  // Add envUrl property to store the baseUrl from config
  private envUrl: string = environment.baseUrl;
  
  // Signals for reactive state management
  private readonly _clientDetails = signal<ClientDetails | null>(null);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _logoInfo = signal<LogoInfo | null>(null);

  // Computed values for easy access
  readonly clientDetails = this._clientDetails.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly logoInfo = this._logoInfo.asReadonly();

  // Computed values for specific properties
  readonly companyName = computed(() => this._clientDetails()?.defaultCompanyName || 'MyFleetMan');
  readonly companyLogo = computed(() => this._clientDetails()?.defaultCompanyLogo || 'assets/my_fleetman_logo.svg');
  readonly clientName = computed(() => this._clientDetails()?.clientName || 'B2C - WEB BOOKING');
  readonly beforeBookingTime = computed(() => this._clientDetails()?.beforeBookingTime || '01:00');
  
  // Computed values for logo handling
  readonly logoUrl = computed(() => this._clientDetails()?.defaultCompanyLogo || 'assets/my_fleetman_logo.svg');
  readonly logoCssClass = computed(() => this._logoInfo()?.cssClass || 'logo-square');
  readonly logoIsValid = computed(() => this._logoInfo()?.isValid ?? true);

  constructor() {
    console.log('ClientDetailsService initialized');
    // Load client details on service initialization
    this.loadClientDetails();
  }

  /**
   * Load client details from the API
   */
  loadClientDetails(): void {
    this._isLoading.set(true);
    this._error.set(null);

    // Use loadSysConfig().subscribe() pattern to get baseUrl from sys-config.json
    this.configService.loadSysConfig(this.platformLocation).subscribe(config => {
      this.envUrl = config.baseUrl;
      
      
      this.fetchClientDetails().pipe(
        tap({
          next: (response) => {
            
            if (response.responseCode === 'OK' && response.data) {
              this._clientDetails.set(response.data);
              
              
              // Analyze logo dimensions
              this.analyzeLogo(response.data.defaultCompanyLogo);
            } else {
              this._error.set(response.responseMessage || 'Failed to load client details');
              console.error('Client details API error:', response.responseMessage);
            }
          },
          error: (error) => {
            console.error('Error loading client details:', error);
            this._error.set('Failed to load client details');
          }
        }),
        catchError((error) => {
          console.error('Error in loadClientDetails:', error);
          this._error.set('Network error while loading client details');
          return of(null);
        })
      ).subscribe(() => {
        this._isLoading.set(false);
      });
    });
  }

  /**
   * Fetch client details from the API
   */
  private fetchClientDetails(): Observable<ClientDetailsResponse> {
    const url = `${this.envUrl}/api/B2COnlineBooking/B2CclientDetails`;
   
    return this.http.get<ClientDetailsResponse>(url);
  }

  /**
   * Analyze logo dimensions and update logo info
   */
  private analyzeLogo(logoUrl: string): void {
    this.logoUtility.getLogoInfo(logoUrl).subscribe({
      next: (logoInfo) => {
        this._logoInfo.set(logoInfo);
        
      },
      error: (error) => {
        console.error('Error analyzing logo:', error);
        // Set fallback logo info
        this._logoInfo.set({
          url: logoUrl,
          width: 100,
          height: 100,
          aspectRatio: 1,
          cssClass: 'logo-square',
          isValid: false
        });
      }
    });
  }

  /**
   * Refresh client details
   */
  refreshClientDetails(): void {
    this.loadClientDetails();
  }

  /**
   * Get client details as an observable (for backward compatibility)
   */
  getClientDetails(): Observable<ClientDetails | null> {
    return new Observable(observer => {
      // Initial value
      observer.next(this.clientDetails());
      
      // Create a subscription to the signal
      const cleanup = effect(() => {
        const details = this.clientDetails();
        observer.next(details);
      });
      
      return () => cleanup.destroy();
    });
  }
} 